// The order of a scorecard's questions. The author sets it with the up/down buttons in the editor
// and the upload review; it is saved as sortOrder 1..N, and the template API, the scorer and the
// reports all read it back from there.

// 0 and 999 (the editor's old placeholder for a new question) mean "never ordered". Those questions
// go after the ordered ones, by name - how every scorecard was shown before it could be reordered.
// The same rule is in template-management.js (sortQuestions) and pca-validations.py
// (effective_question_order); keep the three in step.
const UNORDERED_SORT_ORDERS = [0, 999];

const questionPosition = (question) => {
  const position = Number(question.sortOrder);
  return Number.isFinite(position) && !UNORDERED_SORT_ORDERS.includes(position)
    ? position
    : Number.POSITIVE_INFINITY;
};

// Names compare by character code, not locale: that is how DynamoDB orders the SKs and how the
// scorer breaks ties, so an unordered scorecard reads the same everywhere ("Greeting" before "closing").
const byName = (a, b) => {
  const x = String(a.questionName);
  const y = String(b.questionName);
  return x < y ? -1 : (x > y ? 1 : 0);
};

export const sortQuestions = (questions) =>
  [...questions].sort((a, b) =>
    (questionPosition(a) - questionPosition(b)) || byName(a, b));

// Move the item at `index` by `offset` (-1 up, +1 down). Off either end it is a no-op copy.
export const moveItem = (items, index, offset) => {
  const target = index + offset;
  if (target < 0 || target >= items.length) return [...items];
  const next = [...items];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

// Question cards are keyed by this rather than by position, so a moved card keeps its own inputs.
// It never leaves the browser: withSortOrder drops it.
let lastClientKey = 0;
export const withClientKey = (question) =>
  question.clientKey ? question : { ...question, clientKey: `q${++lastClientKey}` };
export const withClientKeys = (questions) => questions.map(withClientKey);

// The questions as they are saved: numbered 1..N in the order on screen.
export const withSortOrder = (questions) =>
  questions.map(({ clientKey, ...question }, index) => ({ ...question, sortOrder: index + 1 }));
